import { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { GetUserStats } from "../../redux/actions/User";
import { chartScales, chartTooltipStyle } from "../../DashBoard/Components/PagesComponents/Main/chartTheme";
import {
  normalizeChartMonths,
  hasRevenueChartData,
  chartMonthKey,
} from "../utils/revenueChartHelpers";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function UserRevenue() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [totals, setTotals] = useState({ totalRevenue: 0, totalTicketsSold: 0 });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await dispatch(GetUserStats());
      if (result?.success && result.stats) {
        setMonthlyRevenue(result.stats.monthlyRevenue ?? []);
        setTotals({
          totalRevenue: result.stats.totalRevenue ?? 0,
          totalTicketsSold: result.stats.totalTicketsSold ?? 0,
        });
      }
      setLoading(false);
    };
    load();
  }, [dispatch]);

  const chartMonths = useMemo(
    () => normalizeChartMonths(monthlyRevenue, totals.totalRevenue),
    [monthlyRevenue, totals.totalRevenue]
  );

  const chartData = useMemo(() => {
    const labels = chartMonths.map((m) => {
      const parts = String(m.month || "").split(" ");
      return parts[0] || m.month;
    });
    const values = chartMonths.map((m) => Number(m.revenue) || 0);
    const max = Math.max(...values, 1);

    return {
      max,
      data: {
        labels,
        datasets: [
          {
            label: "Sales",
            data: values,
            backgroundColor: "#E52324",
            borderRadius: 8,
            borderSkipped: false,
            minBarLength: 6,
          },
        ],
      },
    };
  }, [chartMonths]);

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          ...chartTooltipStyle,
          callbacks: {
            label: (ctx) => ` $${Number(ctx.raw || 0).toLocaleString()}`,
          },
        },
      },
      scales: {
        ...chartScales,
        y: {
          ...chartScales.y,
          beginAtZero: true,
          suggestedMax: chartData.max * 1.15,
          ticks: {
            ...chartScales.y.ticks,
            callback: (v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`,
          },
        },
      },
    }),
    [chartData.max]
  );

  const hasChartData = hasRevenueChartData(chartMonths, totals.totalRevenue);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
      <div className="border-b border-[#1E293B]/60 bg-[#060D1B] shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <h1 className="text-[22px] sm:text-[28px] text-white" style={{ fontWeight: 800 }}>
            Sales Analytics
          </h1>
          <p className="text-white/30 text-[13px] mt-1">
            Track your ticket sales and revenue
          </p>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 p-5">
            <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1">Total Revenue</p>
            <p className="text-[24px] text-white" style={{ fontWeight: 800 }}>
              ${Number(totals.totalRevenue || 0).toLocaleString()}
            </p>
          </div>
          <div className="rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 p-5">
            <p className="text-[11px] text-white/35 uppercase tracking-wider mb-1">Tickets Sold</p>
            <p className="text-[24px] text-white" style={{ fontWeight: 800 }}>
              {Number(totals.totalTicketsSold || 0).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="rounded-2xl bg-[#0A1628] border border-[#1E293B]/60 p-6">
          <h2 className="text-[18px] text-white mb-6" style={{ fontWeight: 700 }}>
            Revenue Overview
          </h2>
          {loading ? (
            <div className="flex justify-center items-center h-[400px]">
              <div className="w-10 h-10 rounded-full border-2 border-[#E52324]/30 border-t-[#E52324] animate-spin" />
            </div>
          ) : hasChartData ? (
            <div className="h-[400px] min-h-[400px]">
              <Bar
                key={chartMonthKey(chartMonths)}
                options={barOptions}
                data={chartData.data}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-[400px] text-white/30 text-[13px]">
              No sales data yet. Revenue will appear once you start selling tickets.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
